import React, { useEffect, useMemo, useState } from "react";
import { computeIncome, requiredFee, requiredSessions, mtdScope } from "../../shared/calc.js";
import { trackVisit, trackInputChange, trackStart, trackResultView, affiliateHref } from "./analytics.js";

const gbp = (n) =>
  "£" +
  Math.round(Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });

const START = {
  sessionsPerWeek: 15,
  feePerSession: 60,
  costPerSession: 0,
  costPerDay: 0,
  costPerMonth: 300,
};

// --- Affiliate slots -------------------------------------------------------
// Drop the real tracked affiliate link (the one the partner/network issues,
// containing YOUR affiliate id) into `url`, then flip `enabled` to true.
// IMPORTANT: a `utm_source=…` tag is NOT an affiliate link — it earns nothing.
// Keep a slot `enabled: false` until you have a real, paying link, so we never
// send a partner free referral traffic for nothing.
//
// Render a slot anywhere with: <AffiliateLink slot="cardReader" />
// Affiliate CTAs point at clean practik.co.uk/go/<slug> links; the real tracked
// link lives server-side in server/affiliates.js (so users never see the network
// domain and links can change without rebuilding the client). To show a CTA, set
// `enabled: true` here AND paste the real link into server/affiliates.js — until
// then it stays hidden so we don't send a partner free traffic for nothing.
const AFFILIATES = {
  // Tax set-aside CTA → QuickBooks UK (~15%, via CJ). FreeAgent dropped — its
  // schemes don't pay publishers.
  accounting: {
    enabled: false,
    url: "/go/quickbooks",
    label: "Get MTD-ready with QuickBooks →",
  },
  // Card reader — the target user often takes cash. SumUp (via impact.com).
  // image: paste the official impact.com creative/banner image URL here to show
  // a product picture on the card. Until then a calm on-brand placeholder shows.
  cardReader: {
    enabled: true,
    url: "/go/sumup",
    image: "/sumup-solo-lite.png",
    label: "Take card payments with SumUp →",
  },
  // NOTE: therapist PI insurance was considered and dropped — Balens/Howden have
  // no publisher affiliate programme, and paid insurance referrals fall under the
  // FCA Introducer Appointed Representative regime (regulated, not a simple link).
};

// Deep link: `/?start=1` opens the three questions (Step 1 of 3) straight away,
// skipping the landing screen. Used by CTAs on pages that have already done the
// selling — e.g. practice-planner-reviews.uk — so the click lands on the first
// question rather than a second pitch. Any other entry still starts on landing.
function startScreen() {
  if (typeof window === "undefined") return "landing";
  const v = new URLSearchParams(window.location.search).get("start");
  return v !== null && v !== "0" && v !== "false" ? "input" : "landing";
}

export default function App() {
  const [screen, setScreen] = useState(startScreen); // landing | input | result
  const [data, setData] = useState(START);

  useEffect(() => {
    trackVisit();
  }, []);

  // The funnel that matters is visit → result, so the result screen reports
  // itself. Keyed on the screen rather than mounted inside Result, because
  // Result stays mounted while the sliders move; trackResultView dedupes per
  // browser session, so leaving and coming back doesn't count twice.
  useEffect(() => {
    if (screen === "result") trackResultView();
  }, [screen]);

  // Same idea one step earlier: the input screen reports itself, so a visit
  // that never gets past the landing page is distinguishable from one that
  // starts the questions and gives up partway.
  useEffect(() => {
    if (screen === "input") trackStart();
  }, [screen]);

  const r = useMemo(() => computeIncome(data), [data]);

  return (
    <div className="app">
      <button className="brand no-print" onClick={() => setScreen("landing")} aria-label="Back to the start">
        <span className="dot" /> Practik
      </button>

      {screen === "landing" && (
        <Landing onStart={() => setScreen("input")} />
      )}
      {screen === "input" && (
        <Inputs
          data={data}
          setData={setData}
          onDone={() => setScreen("result")}
          onBack={() => setScreen("landing")}
        />
      )}
      {screen === "result" && (
        <Result
          data={data}
          setData={setData}
          r={r}
          onBack={() => setScreen("input")}
        />
      )}
    </div>
  );
}

/* ----------------------------- Landing ----------------------------- */
function Landing({ onStart }) {
  return (
    <div className="hero">
      <div className="kicker">New to private practice?</div>
      <h1>See what you’ll actually take home</h1>
      <p className="what">
        Setting up on your own — or rethinking your fees — means a lot of guessing
        about tax, costs and what’s actually left over. Practik turns it into one
        clear number. Answer three simple questions and see exactly what you keep
        after tax, National Insurance and running costs — in about a minute, no
        spreadsheets.
      </p>

      <img
        className="hero-shot"
        src="/result-preview.png"
        width="760"
        height="680"
        alt="Practik showing an estimated monthly take-home pay of £2,603, split into what you keep, tax and NI, and running costs"
      />

      <button className="btn hero-cta" onClick={onStart}>
        Calculate my take-home →
      </button>

      <ul className="trust-row">
        <li>✓ Free</li>
        <li>✓ No sign-up</li>
        <li>✓ No personal data</li>
        <li>✓ 2025/26 UK tax rates</li>
      </ul>

      <ul className="value-pillars">
        <li>
          <span className="pill-emoji">💷</span>
          <span><strong>See your exact take-home</strong> — after tax, NI and running costs.</span>
        </li>
        <li>
          <span className="pill-emoji">🪙</span>
          <span><strong>Know what to set aside for tax</strong> — to the pound, every month.</span>
        </li>
        <li>
          <span className="pill-emoji">🎯</span>
          <span><strong>Work out what to charge</strong> — to hit the take-home you want.</span>
        </li>
      </ul>

      {/* Crawl paths. The earnings pages are static HTML generated into
          client/public by scripts/build-pages.mjs; the review lives on its own
          domain. An SPA gives crawlers no other way in, so these links matter
          more than they look. */}
      <p className="guide-link">
        <a href="/earnings/">What therapists actually earn, after tax →</a>
      </p>
      <p className="guide-link">
        From our reviews site:{" "}
        <a
          href="https://practice-planner-reviews.uk/"
          target="_blank"
          rel="noopener"
        >
          the 5 best practice management tools for UK therapists →
        </a>
      </p>
    </div>
  );
}

/* ------------------------------ Inputs ----------------------------- */
function Inputs({ data, setData, onDone, onBack }) {
  const [step, setStep] = useState(0);
  const set = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    trackInputChange(key, value);
  };
  const steps = [
    {
      key: "sessionsPerWeek",
      label: "Step 1 of 3",
      question: "How many client sessions do you see in a typical week?",
      hint: "A rough number is fine — you can change it later.",
      unit: "sessions / week",
      stepBy: 1,
      min: 0,
      max: 60,
    },
    {
      key: "feePerSession",
      label: "Step 2 of 3",
      question: "What do you charge for one session?",
      hint: "Your usual fee. Pop in your average if it varies.",
      unit: "per session",
      stepBy: 5,
      min: 0,
      max: 400,
      money: true,
    },
    { key: "costs", label: "Step 3 of 3", costs: true },
  ];

  const current = steps[step];
  const last = step === steps.length - 1;

  return (
    <div className="card mt">
      <div className="step-count">{current.label}</div>

      {current.costs ? (
        <CostsStep data={data} set={set} />
      ) : (
        <>
          <h2 className="question">{current.question}</h2>
          <p className="hint">{current.hint}</p>
          <Stepper
            value={data[current.key]}
            onChange={(v) => set(current.key, v)}
            stepBy={current.stepBy}
            min={current.min}
            max={current.max}
            money={current.money}
            unit={current.unit}
          />
        </>
      )}

      <div className="nav-row">
        <button className="btn ghost" onClick={() => (step === 0 ? onBack() : setStep(step - 1))}>
          ← Back
        </button>
        <button className="btn" onClick={() => (last ? onDone() : setStep(step + 1))}>
          {last ? "Show my income" : "Next →"}
        </button>
      </div>
    </div>
  );
}

function Stepper({ value, onChange, stepBy = 1, min = 0, max = 999, money, unit }) {
  const clamp = (v) => Math.min(max, Math.max(min, v));
  return (
    <div className="stepper">
      <button onClick={() => onChange(clamp(value - stepBy))} aria-label="Decrease">
        −
      </button>
      <div className="value">
        <StepperNum value={value} money={money} onChange={(n) => onChange(clamp(n))} unit={unit} />
        <span className="unit">{unit}</span>
      </div>
      <button onClick={() => onChange(clamp(value + stepBy))} aria-label="Increase">
        +
      </button>
    </div>
  );
}

// The number is a real text field so you can just type your fee, not only tap +/-.
// Keeps a local string while editing (so the box can be empty mid-type), and
// resyncs from the value when the +/- buttons change it or on blur.
function StepperNum({ value, money, onChange, unit }) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText(String(value));
  }, [value]);
  return (
    <div className="num">
      {money && <span className="cur">£</span>}
      <input
        className="num-input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={text}
        aria-label={`Edit ${unit || "value"}`}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^0-9]/g, "");
          setText(digits);
          onChange(digits === "" ? 0 : Number(digits));
        }}
        onBlur={() => setText(String(value))}
      />
    </div>
  );
}

// An editable £ amount you can just type — so a value can't get stuck at £0 when
// a slider handle is hard to grab (e.g. on a touchscreen). The slider still nudges.
function MoneyField({ value, onChange, max }) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText(String(value));
  }, [value]);
  return (
    <span className="val val-edit">
      £
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label="Edit amount"
        value={text}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^0-9]/g, "");
          setText(digits);
          onChange(digits === "" ? 0 : Math.min(max, Number(digits)));
        }}
        onBlur={() => setText(String(value))}
      />
    </span>
  );
}

function CostsStep({ data, set }) {
  const fields = [
    { key: "costPerMonth", label: "Costs every month", eg: "e.g. room rent, supervision, insurance, software, advertising", max: 3000, step: 25 },
    { key: "costPerSession", label: "Costs for each session", eg: "e.g. room rate, materials", max: 60, step: 5 },
    { key: "costPerDay", label: "Costs for each day you work", eg: "e.g. travel, parking", max: 100, step: 5 },
  ];
  return (
    <>
      <h2 className="question">What does it cost you to practise?</h2>
      <p className="hint">Tap a figure to type it in, or drag the slider. Leave the rest blank.</p>
      {fields.map((f) => (
        <div className="slider-row" key={f.key}>
          <div className="top">
            <span className="name">
              {f.label}
              <span style={{ display: "block", fontWeight: 600, color: "var(--ink-soft)", fontSize: 13 }}>
                {f.eg}
              </span>
            </span>
            <MoneyField value={data[f.key]} max={f.max} onChange={(v) => set(f.key, v)} />
          </div>
          <input
            type="range"
            min="0"
            max={f.max}
            step={f.step}
            value={data[f.key]}
            onChange={(e) => set(f.key, Number(e.target.value))}
          />
          <div className="slider-scale" aria-hidden="true">
            <span>{gbp(0)}</span>
            <span>{gbp(f.max / 2)}</span>
            <span>{gbp(f.max)}</span>
          </div>
        </div>
      ))}
    </>
  );
}

/* ------------------------------ Result ----------------------------- */
function Result({ data, setData, r, onBack }) {
  const [period, setPeriod] = useState("month");
  const [showDetail, setShowDetail] = useState(false);
  const set = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    trackInputChange(key, value);
  };

  const takeHome =
    period === "year" ? r.takeHome : period === "month" ? r.takeHomeMonthly : r.takeHomeWeekly;
  const perLabel = period === "year" ? "a year" : period === "month" ? "a month" : "a week";

  const taxTotal = r.tax + r.nationalInsurance;
  const total = Math.max(1, r.takeHome + r.costs + taxTotal);
  const pct = (n) => (Math.max(0, n) / total) * 100;

  return (
    <div className="card mt">
      <div className="result-hero">
        <div className="label">Your estimated take-home pay</div>
        <div className="big">{gbp(takeHome < 0 ? 0 : takeHome)}</div>
        <div className="per">{perLabel}</div>
        {takeHome < 0 && (
          <p className="hint" style={{ marginTop: 10 }}>
            Your costs are higher than your income right now — try the sliders below.
          </p>
        )}
      </div>

      <div className="center">
        <div className="period-toggle">
          {["week", "month", "year"].map((p) => (
            <button key={p} className={period === p ? "active" : ""} onClick={() => setPeriod(p)}>
              Per {p}
            </button>
          ))}
        </div>
      </div>

      <div className="moneybar">
        <span className="seg-take" style={{ flexGrow: pct(r.takeHome) }} />
        <span className="seg-tax" style={{ flexGrow: pct(taxTotal) }} />
        <span className="seg-cost" style={{ flexGrow: pct(r.costs) }} />
      </div>
      <div className="legend">
        <div className="item"><span className="swatch seg-take" /> You keep</div>
        <div className="item"><span className="swatch seg-tax" /> Tax &amp; NI</div>
        <div className="item"><span className="swatch seg-cost" /> Running costs</div>
      </div>

      <div className="adjust">
        <Slider name="Fee per session" value={data.feePerSession} display={gbp(data.feePerSession)} min={0} max={400} step={1} onChange={(v) => set("feePerSession", v)} />
        <Slider name="Sessions per week" value={data.sessionsPerWeek} display={`${data.sessionsPerWeek}`} min={0} max={60} step={1} onChange={(v) => set("sessionsPerWeek", v)} />
        <Slider name="Monthly running costs" value={data.costPerMonth} display={gbp(data.costPerMonth)} min={0} max={3000} step={1} onChange={(v) => set("costPerMonth", v)} />
      </div>

      {!showDetail ? (
        <div className="center mt">
          <button className="btn detail-cta" onClick={() => setShowDetail(true)}>
            Show the detail (including tax) ↓
          </button>
        </div>
      ) : (
        <Detail r={r} onHide={() => setShowDetail(false)} period={period} />
      )}

      {/* ---------------- Tax set-aside (free) ---------------- */}
      <SetAside r={r} />

      {/* ---------------- Making Tax Digital note (free) ---------------- */}
      <MtdNote r={r} />

      {/* ---------------- Goal planner (free, no gate) ---------------- */}
      <GoalPlanner data={data} r={r} />

      {/* ---------------- Card reader (affiliate) — moved to the bottom ---------------- */}
      <CardReaderCard />

      <div className="nav-row">
        <button className="btn ghost" onClick={onBack}>
          ← Change my answers
        </button>
      </div>

      <p className="disclaimer">
        Figures are a friendly estimate based on 2025/26 UK self-employed rates
        (England, Wales &amp; NI). Practik isn’t financial or tax advice.
      </p>
    </div>
  );
}

function Slider({ name, value, display, min, max, step, onChange }) {
  return (
    <div className="slider-row">
      <div className="top">
        <span className="name">{name}</span>
        <span className="val">{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function Detail({ r, onHide, period }) {
  // Scale the annual breakdown to the period chosen by the Per week/month/year
  // toggle, using the same basis as the hero (month = ÷12, week = ÷weeksPerYear).
  const factor = period === "year" ? 1 : period === "month" ? 1 / 12 : 1 / r.inputs.weeksPerYear;
  const per = period === "year" ? "year" : period === "month" ? "month" : "week";
  const v = (n) => gbp(n * factor);
  return (
    <div className="detail">
      <div className="line"><span>Income from sessions</span><span>{v(r.gross)}</span></div>
      <div className="line"><span className="muted">Less running costs</span><span className="muted">− {v(r.costs)}</span></div>
      <div className="line"><span>Profit before tax</span><span>{v(r.profit)}</span></div>
      <div className="line"><span className="muted">Less income tax (estimate)</span><span className="muted">− {v(r.tax)}</span></div>
      <div className="line"><span className="muted">Less National Insurance (estimate)</span><span className="muted">− {v(r.nationalInsurance)}</span></div>
      <div className="line total"><span>Your take-home pay</span><span>{v(r.takeHome)} / {per}</span></div>
      <p className="sub">
        These figures are for the period you’ve selected above, and assume{" "}
        {r.inputs.weeksPerYear} working weeks a year. The tax estimate is a simple
        guide — your accountant or an HMRC self-assessment will give the exact
        number.
      </p>
      <div className="center" style={{ marginTop: 12 }}>
        <button className="btn ghost" onClick={onHide}>Hide the detail ↑</button>
      </div>
    </div>
  );
}

/* ----------------------------- Affiliate link ----------------------------- */
// Renders nothing until its slot has a real link and is enabled — so an
// unconfigured slot never ships a dead or unpaid link.
function AffiliateLink({ slot, className = "affiliate" }) {
  const aff = AFFILIATES[slot];
  if (!aff || !aff.enabled || !aff.url) return null;
  return (
    <a className={className} href={affiliateHref(aff.url)} target="_blank" rel="sponsored noopener noreferrer">
      {aff.label}
    </a>
  );
}

/* ------------------------- Tax set-aside (free) ------------------------- */
function SetAside({ r }) {
  if (r.profit <= 0) return null;
  return (
    <div className="feature-card setaside">
      <div className="feature-head">
        <span className="emoji">🪙</span>
        <h3>Put this aside for tax</h3>
      </div>
      <p className="feature-lead">
        Tuck away <strong>{gbp(r.setAsideMonthly)} a month</strong> (about{" "}
        {gbp(r.setAsidePerSession)} per session) and your tax bill shouldn’t
        catch you out. That covers your income tax and National Insurance —
        roughly {r.effectiveTaxRate}% of your profit.
      </p>
    </div>
  );
}

/* ------------------- Making Tax Digital note (free) ------------------- */
// Calm, plain-English heads-up that self-employed income tax is going digital,
// personalised to whether the practitioner's GROSS income likely brings them
// into MTD and from when. Positions Practik as the friendly first step and
// routes to compatible accounting software (QuickBooks) via the affiliate slot.
function MtdNote({ r }) {
  if (r.gross <= 0) return null;
  const mtd = mtdScope(r.gross);
  return (
    <div className="feature-card">
      <div className="feature-head">
        <span className="emoji">📲</span>
        <h3>Tax is going digital</h3>
      </div>
      <p className="feature-lead">
        {mtd.inScope ? (
          <>
            From <strong>{mtd.from}</strong>, HMRC’s Making Tax Digital means
            keeping your records digitally and sending a short update every three
            months — instead of one big tax return once a year. At your level of
            income, you’re likely in this group.
          </>
        ) : (
          <>
            HMRC’s Making Tax Digital is bringing in quarterly digital updates for
            self-employed income tax. At your current income you’re not in scope
            yet — but it’s worth knowing about as your practice grows.
          </>
        )}
      </p>
      <p className="feature-lead">
        You won’t need a spreadsheet for it. Compatible accounting software keeps
        the records and files the updates for you, so it stays a five-minute job
        rather than a looming one.
      </p>
      <AffiliateLink slot="accounting" />
      <p style={{ color: "var(--ink-soft)", fontSize: 13, marginTop: 12, lineHeight: 1.5 }}>
        A rough guide based on HMRC’s announced timetable — check the current
        rules for your own situation.
      </p>
    </div>
  );
}

/* ---------------------- Practik payoff + card-payment tip ---------------------- */
// Leads with Practik's own benefit (you now know your numbers), then offers
// genuine category advice — taking payment on the spot — as plain body text with
// a subtle text link, the same pattern as the tax card's affiliate link (advice +
// quiet link, no promo box, no brand name). The link still goes via /go/sumup, so
// it's tracked and earns. Only renders the tip when the cardReader slot is enabled.
function CardReaderCard() {
  const aff = AFFILIATES.cardReader;
  if (!aff || !aff.enabled || !aff.url) return null;
  // Both the banner and the button are the same tracked link, so they carry the
  // same click id — /go/sumup counts the click, not the client.
  const href = affiliateHref(aff.url);
  return (
    <div className="feature-card cardreader">
      <div className="feature-head">
        <span className="emoji">💳</span>
        <h3>Take control of your fees</h3>
      </div>
      <p className="feature-lead">
        A SumUp reader takes payment on the spot, so every session is settled the
        moment it ends — no spreadsheet to reconcile, no client to chase, and no
        more “I’ll get on top of my money one day.”
      </p>
      <p className="feature-lead">
        Add SumUp’s free Business Account and your takings land the next day —
        even weekends and bank holidays — kept separate from your personal money.
        You’re just in control, without lifting a finger.
      </p>
      {aff.image && (
        <a className="cardreader-banner" href={href} target="_blank" rel="sponsored noopener noreferrer">
          <img src={aff.image} alt="SumUp card reader" loading="lazy" />
        </a>
      )}
      <a className="btn" href={href} target="_blank" rel="sponsored noopener noreferrer">
        Get a SumUp reader &amp; account →
      </a>
    </div>
  );
}

/* ---------------------- Goal planner (free, no gate) ---------------------- */
function GoalPlanner({ data, r }) {
  const baseline = Math.max(1000, Math.ceil((r.takeHomeMonthly + 1) / 250) * 250);
  const [target, setTarget] = useState(baseline + 250);
  const [mode, setMode] = useState("fee"); // fee | sessions

  const annualTarget = target * 12;
  const fee = requiredFee(annualTarget, data);
  const sessions = requiredSessions(annualTarget, data);

  return (
    <div className="feature-card">
      <div className="feature-head">
        <span className="emoji">🎯</span>
        <h3>Reach a take-home goal</h3>
      </div>
      <p className="feature-lead">I want to take home…</p>
      <div className="stepper small">
        <button onClick={() => setTarget((t) => Math.max(250, t - 250))}>−</button>
        <div className="value">
          <div className="num">{gbp(target)}</div>
          <span className="unit">a month</span>
        </div>
        <button onClick={() => setTarget((t) => t + 250)}>+</button>
      </div>

      <div className="center">
        <div className="period-toggle">
          <button className={mode === "fee" ? "active" : ""} onClick={() => setMode("fee")}>Adjust my fee</button>
          <button className={mode === "sessions" ? "active" : ""} onClick={() => setMode("sessions")}>Adjust my sessions</button>
        </div>
      </div>

      <div className="goal-answer">
        {mode === "fee" ? (
          <>
            Charge{" "}
            <strong>{fee.capped ? `more than ${gbp(fee.value)}` : gbp(fee.value)}</strong> per session
            <span className="goal-sub">at your current {data.sessionsPerWeek} sessions a week</span>
          </>
        ) : (
          <>
            See{" "}
            <strong>{sessions.capped ? `more than ${sessions.value}` : sessions.value}</strong> sessions a week
            <span className="goal-sub">at your current {gbp(data.feePerSession)} fee</span>
          </>
        )}
      </div>
    </div>
  );
}



